import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

interface PreviewData {
  title: string | null;
  description: string | null;
  image: string | null;
  url: string;
  publisher: string | null;
}

// Module-level cache so previews survive re-renders
const previewCache = new Map<string, PreviewData | null>();

async function fetchPreview(url: string): Promise<PreviewData | null> {
  if (previewCache.has(url)) return previewCache.get(url)!;
  try {
    const res = await fetch(
      `https://api.microlink.io?url=${encodeURIComponent(url)}&palette=false&adblock=false`,
      { signal: AbortSignal.timeout(5000) },
    );
    if (!res.ok) { previewCache.set(url, null); return null; }
    const json = await res.json();
    if (json.status !== 'success') { previewCache.set(url, null); return null; }
    const d = json.data;
    const preview: PreviewData = {
      title: d.title || null,
      description: d.description || null,
      image: d.image?.url || null,
      url: d.url || url,
      publisher: d.publisher || null,
    };
    previewCache.set(url, preview);
    return preview;
  } catch {
    previewCache.set(url, null);
    return null;
  }
}

// Simple URL extractor — excludes YouTube, GIFs, and media URLs already rendered
const PLAIN_URL_REGEX =
  /https?:\/\/(?!(?:www\.)?(?:youtube\.com|youtu\.be|media\.giphy\.com|tenor\.com))[^\s<>"]+/gi;
const MEDIA_EXT_REGEX = /\.(gif|jpe?g|png|webp|svg|mp4|webm)(\?.*)?$/i;

export function extractPreviewUrl(text: string): string | null {
  const matches = text.match(PLAIN_URL_REGEX) ?? [];
  const candidate = matches.find((u) => !MEDIA_EXT_REGEX.test(u.split('?')[0]));
  return candidate ?? null;
}

function LinkPreviewCard({ url }: { url: string }) {
  const cached = previewCache.has(url) ? previewCache.get(url) : undefined;
  const [preview, setPreview] = useState<PreviewData | null | undefined>(cached);

  useEffect(() => {
    if (preview !== undefined) return;
    fetchPreview(url).then(setPreview);
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  if (preview === undefined) {
    // Loading skeleton
    return (
      <Box
        sx={{
          mt: 1.5,
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Skeleton variant="rectangular" height={140} />
        <Box sx={{ p: 1.5 }}>
          <Skeleton width="45%" height={14} sx={{ mb: 0.5 }} />
          <Skeleton width="90%" height={14} />
          <Skeleton width="65%" height={14} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
    );
  }

  if (!preview || (!preview.title && !preview.image)) return null;

  return (
    <Box
      component="a"
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'block',
        mt: 1.5,
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          borderColor: 'primary.main',
        },
      }}
    >
      {preview.image && (
        <Box
          component="img"
          src={preview.image}
          alt={preview.title || 'Link preview'}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          sx={{
            width: '100%',
            maxHeight: 200,
            objectFit: 'cover',
            display: 'block',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}
      <Box sx={{ px: 1.5, py: 1 }}>
        {preview.publisher && (
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
              color: 'text.disabled',
              mb: 0.25,
            }}
          >
            {preview.publisher}
          </Typography>
        )}
        {preview.title && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 700,
              lineHeight: 1.3,
              color: 'text.primary',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {preview.title}
          </Typography>
        )}
        {preview.description && (
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: 'text.secondary',
              lineHeight: 1.4,
              mt: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {preview.description}
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: '0.65rem',
            color: 'text.disabled',
            mt: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {preview.url}
        </Typography>
      </Box>
    </Box>
  );
}

export default LinkPreviewCard;
